import { useEffect, useState } from 'react';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { addPageAction, noticeError } from '../newrelic';

export default function Chaos() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [lastResponse, setLastResponse] = useState(null);
  const toast = useToast();

  // Form states
  const [latencyMs, setLatencyMs] = useState(500);
  const [persistentLatencyMs, setPersistentLatencyMs] = useState(200);
  const [errorCode, setErrorCode] = useState(500);
  const [errorRate, setErrorRate] = useState(25);
  const [memoryMb, setMemoryMb] = useState(50);
  const [cpuDuration, setCpuDuration] = useState(1000);
  const [cpuThreads, setCpuThreads] = useState(1);
  const [logCount, setLogCount] = useState(100);
  const [logLevel, setLogLevel] = useState('INFO');

  const fetchStatus = async () => {
    try {
      const res = await api.getChaosStatus();
      setStatus(res.data);
    } catch (err) {
      noticeError(err, { page: 'chaos', action: 'fetchStatus' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    addPageAction('chaos_page_viewed');
    fetchStatus();
  }, []);

  const exec = async (key, fn, actionName) => {
    setBusy((prev) => ({ ...prev, [key]: true }));
    setLastResponse(null);
    try {
      const res = await fn();
      addPageAction(actionName);
      setLastResponse(res);
      toast(`${actionName} completed`, 'success');
      await fetchStatus();
    } catch (err) {
      noticeError(err, { page: 'chaos', action: actionName });
      setLastResponse(err.data || { error: err.message });
      toast(err.message, 'error');
    } finally {
      setBusy((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  const isEnabled = status?.enabled ?? false;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Chaos Engineering</h1>
        <p className="page-subtitle">Inject failures and stress conditions for observability testing</p>
      </div>

      {/* Status Bar */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div>
              <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Status </span>
              <StatusBadge status={isEnabled ? 'ACTIVE' : 'INACTIVE'} />
            </div>
            <div>
              <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Error Rate: </span>
              <strong>{status?.errorRate ?? 0}%</strong>
            </div>
            <div>
              <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Latency: </span>
              <strong>{status?.latencyMs ?? 0}ms</strong>
            </div>
            <div>
              <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Memory Blocks: </span>
              <strong>{status?.memoryPressureBlocks ?? 0}</strong>
            </div>
          </div>
          <div className="actions">
            <button
              className={`btn btn-sm ${isEnabled ? 'btn-danger' : 'btn-success'}`}
              disabled={busy.toggle}
              onClick={() => exec('toggle', () => api.toggleChaos(!isEnabled), 'chaos_toggled')}
            >
              {isEnabled ? 'Disable' : 'Enable'}
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={busy.reset}
              onClick={() => exec('reset', api.resetChaos, 'chaos_reset')}
            >
              Reset All
            </button>
          </div>
        </div>
      </div>

      <div className="grid-2">
        {/* Latency */}
        <div className="card">
          <div className="card-title">Latency Injection</div>
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0.5rem 0 1rem' }}>
            Add artificial delay to test timeout alerts and latency dashboards.
          </p>
          <div className="form-group">
            <label className="label">One-time Delay (ms)</label>
            <div className="actions">
              <input
                className="input"
                type="number"
                min={0}
                max={5000}
                value={latencyMs}
                onChange={(e) => setLatencyMs(Number(e.target.value))}
                style={{ width: 120 }}
              />
              <button
                className="btn btn-primary btn-sm"
                disabled={busy.latency}
                onClick={() => exec('latency', () => api.injectLatency(latencyMs), 'latency_injected')}
              >
                {busy.latency ? <span className="spinner" /> : 'Inject'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="label">Persistent Latency (ms)</label>
            <div className="actions">
              <input
                className="input"
                type="number"
                min={0}
                max={5000}
                value={persistentLatencyMs}
                onChange={(e) => setPersistentLatencyMs(Number(e.target.value))}
                style={{ width: 120 }}
              />
              <button
                className="btn btn-warning btn-sm"
                disabled={busy.pLatency}
                onClick={() =>
                  exec('pLatency', () => api.setPersistentLatency(persistentLatencyMs), 'persistent_latency_set')
                }
              >
                Set
              </button>
            </div>
          </div>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy.rLatency}
            onClick={() => exec('rLatency', () => api.randomLatency(), 'random_latency')}
          >
            Random Latency (100–1000ms)
          </button>
        </div>

        {/* Errors */}
        <div className="card">
          <div className="card-title">Error Injection</div>
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0.5rem 0 1rem' }}>
            Trigger HTTP errors and set failure rates for error-rate alert testing.
          </p>
          <div className="form-group">
            <label className="label">HTTP Error Code</label>
            <div className="actions">
              <select
                className="input"
                value={errorCode}
                onChange={(e) => setErrorCode(Number(e.target.value))}
                style={{ width: 120 }}
              >
                <option value={400}>400</option>
                <option value={403}>403</option>
                <option value={404}>404</option>
                <option value={500}>500</option>
                <option value={502}>502</option>
                <option value={503}>503</option>
              </select>
              <button
                className="btn btn-danger btn-sm"
                disabled={busy.error}
                onClick={() => exec('error', () => api.injectError(errorCode), 'error_injected')}
              >
                Trigger
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="label">Persistent Error Rate (%)</label>
            <div className="actions">
              <input
                className="input"
                type="number"
                min={0}
                max={100}
                value={errorRate}
                onChange={(e) => setErrorRate(Number(e.target.value))}
                style={{ width: 120 }}
              />
              <button
                className="btn btn-warning btn-sm"
                disabled={busy.eRate}
                onClick={() => exec('eRate', () => api.setErrorRate(errorRate), 'error_rate_set')}
              >
                Set
              </button>
            </div>
          </div>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy.rError}
            onClick={() => exec('rError', api.randomError, 'random_error')}
          >
            Test Random Error
          </button>
        </div>

        {/* Resource Pressure */}
        <div className="card">
          <div className="card-title">Resource Pressure</div>
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0.5rem 0 1rem' }}>
            Simulate memory and CPU pressure for resource alert testing.
          </p>
          <div className="form-group">
            <label className="label">Memory Allocation (MB)</label>
            <div className="actions">
              <input
                className="input"
                type="number"
                min={1}
                max={512}
                value={memoryMb}
                onChange={(e) => setMemoryMb(Number(e.target.value))}
                style={{ width: 120 }}
              />
              <button
                className="btn btn-danger btn-sm"
                disabled={busy.memAlloc}
                onClick={() => exec('memAlloc', () => api.allocateMemory(memoryMb), 'memory_allocated')}
              >
                Allocate
              </button>
            </div>
          </div>
          <button
            className="btn btn-success btn-sm"
            disabled={busy.memRelease}
            onClick={() => exec('memRelease', api.releaseMemory, 'memory_released')}
            style={{ marginBottom: '1rem' }}
          >
            Release Memory
          </button>
          <div className="form-group">
            <label className="label">CPU Load</label>
            <div className="actions">
              <input
                className="input"
                type="number"
                min={100}
                max={10000}
                value={cpuDuration}
                onChange={(e) => setCpuDuration(Number(e.target.value))}
                style={{ width: 100 }}
                placeholder="ms"
              />
              <input
                className="input"
                type="number"
                min={1}
                max={8}
                value={cpuThreads}
                onChange={(e) => setCpuThreads(Number(e.target.value))}
                style={{ width: 70 }}
                placeholder="threads"
              />
              <button
                className="btn btn-danger btn-sm"
                disabled={busy.cpu}
                onClick={() => exec('cpu', () => api.cpuLoad(cpuDuration, cpuThreads), 'cpu_load_generated')}
              >
                {busy.cpu ? <span className="spinner" /> : 'Run'}
              </button>
            </div>
          </div>
        </div>

        {/* Log Flooding */}
        <div className="card">
          <div className="card-title">Log Flooding</div>
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0.5rem 0 1rem' }}>
            Generate log bursts to test log aggregation and rate limiting.
          </p>
          <div className="form-group">
            <label className="label">Log Count</label>
            <input
              className="input"
              type="number"
              min={1}
              max={10000}
              value={logCount}
              onChange={(e) => setLogCount(Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label className="label">Level</label>
            <select
              className="input"
              value={logLevel}
              onChange={(e) => setLogLevel(e.target.value)}
            >
              <option value="TRACE">TRACE</option>
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARN">WARN</option>
              <option value="ERROR">ERROR</option>
            </select>
          </div>
          <div className="actions" style={{ gap: '0.5rem' }}>
            <button
              className="btn btn-warning btn-sm"
              disabled={busy.flood}
              onClick={() =>
                exec('flood', () => api.floodLogs(logCount, logLevel, 'Chaos log flood'), 'logs_flooded')
              }
            >
              {busy.flood ? <span className="spinner" /> : 'Flood Logs'}
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={busy.structured}
              onClick={() => exec('structured', () => api.structuredLogs(logCount), 'structured_logs')}
            >
              Structured Logs
            </button>
          </div>
        </div>
      </div>

      {/* Last response */}
      {lastResponse && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-title">Last Response</div>
          <pre className="json" style={{ marginTop: '0.5rem' }}>
            {JSON.stringify(lastResponse, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
}
